import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Wrench, Plus, Camera, Calendar, Gauge, FileText, Trash2, Edit2, Archive, ArchiveRestore,
  ChevronDown, ChevronUp, AlertTriangle, CheckCircle, Clock, Car, 
  Truck, Hammer, HelpCircle, X, Upload, Download
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Translations
const translations = {
  fr: {
    title: 'Entretien Équipements',
    addEquipment: 'Ajouter un équipement',
    editEquipment: 'Modifier l\'équipement',
    name: 'Nom / Désignation',
    type: 'Type',
    vehicle: 'Véhicule',
    excavator: 'Engin / Mini pelle',
    tool: 'Outil motorisé',
    other: 'Autre',
    registration: 'Immatriculation',
    purchaseDate: 'Date d\'achat',
    manufacturingDate: 'Date de fabrication',
    ctDate: 'Date contrôle technique',
    ctOverdue: 'CT dépassé',
    ctOk: 'CT valide',
    pollutionDate: 'Date contrôle pollution',
    pollutionOverdue: 'Pollution dépassé',
    pollutionOk: 'Pollution valide',
    pollutionWarning: 'Pollution bientôt',
    currentMileage: 'Kilométrage actuel',
    currentHours: 'Heures actuelles',
    maintenanceInterval: 'Intervalle entretien (km)',
    maintenanceIntervalHours: 'Intervalle entretien (heures)',
    sparePartsRefs: 'Références pièces d\'entretien',
    sparePartsPhotos: 'Photos des références',
    notes: 'Notes',
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    confirmDelete: 'Voulez-vous vraiment supprimer cet équipement ?',
    archive: 'Archiver',
    unarchive: 'Réactiver',
    confirmArchive: 'Archiver cet équipement (vendu / hors service) ? Il sortira de la liste active mais son historique sera conservé.',
    archived: 'Archivé',
    showArchived: 'Voir les archivés',
    hideArchived: 'Masquer les archivés',
    archivedSection: 'Matériel archivé',
    maintenanceHistory: 'Historique d\'entretien',
    soldOn: 'Vendu le',
    soldDatePrompt: 'Date de vente (AAAA-MM-JJ), vide = aujourd\'hui :',
    addMaintenance: 'Ajouter un entretien',
    editMaintenance: 'Modifier l\'entretien',
    clickToAddFile: 'Cliquer pour ajouter une photo ou un PDF',
    pdfDocument: 'Document PDF',
    maintenanceDate: 'Date de l\'entretien',
    maintenanceType: 'Type d\'entretien (plusieurs choix possibles)',
    oilChange: 'Vidange',
    oilFilter: 'Filtre à huile',
    airFilter: 'Filtre à air',
    fuelFilter: 'Filtre à gasoil',
    cabinFilter: 'Filtre habitacle',
    brakes: 'Freins',
    tires: 'Pneus',
    technicalControl: 'Contrôle technique',
    repair: 'Réparation',
    otherMaintenance: 'Autre',
    mileageAtMaintenance: 'Kilométrage',
    hoursAtMaintenance: 'Heures',
    description: 'Description',
    invoice: 'Facture',
    cost: 'Coût (€)',
    kmRemaining: 'km restants',
    hoursRemaining: 'h restantes',
    kmOverdue: 'km de dépassement',
    hoursOverdue: 'h de dépassement',
    maintenanceOk: 'Entretien à jour',
    maintenanceWarning: 'Entretien bientôt',
    maintenanceOverdue: 'Entretien dépassé',
    updateMileage: 'Mettre à jour le kilométrage',
    updateHours: 'Mettre à jour les heures',
    updateKmIn: 'Prochaine mise à jour du kilométrage dans',
    updateHoursIn: 'Prochaine mise à jour des heures dans',
    updateDueNow: 'Mise à jour à faire maintenant',
    daysWord: 'jours',
    noEquipment: 'Aucun équipement enregistré',
    photo: 'Photo',
    clickToAddPhoto: 'Cliquer pour ajouter une photo',
    loading: 'Chargement...',
    error: 'Erreur',
    success: 'Succès',
    mileageUpdatedThanks: 'Le kilométrage est mis à jour. Merci !',
    hoursUpdatedThanks: 'Les heures sont mises à jour. Merci !',
    km: 'km',
    hours: 'h',
    trackingUnit: 'Unité de suivi',
    trackingKm: 'Kilomètres',
    trackingHours: 'Heures',
    lastMaintenance: 'Dernier entretien',
    nextMaintenance: 'Prochain entretien',
    enterNewMileage: 'Entrez le nouveau kilométrage',
    enterNewHours: 'Entrez les nouvelles heures',
    previous: 'Ancien',
    mileageMustBeHigher: 'Le nouveau kilométrage doit être supérieur à l\'ancien',
    hoursMustBeHigher: 'Les nouvelles heures doivent être supérieures aux anciennes',
  },
  ru: {
    title: 'Обслуживание оборудования',
    addEquipment: 'Добавить оборудование',
    editEquipment: 'Редактировать оборудование',
    name: 'Название',
    type: 'Тип',
    vehicle: 'Транспорт',
    excavator: 'Экскаватор / Мини-экскаватор',
    tool: 'Моторизованный инструмент',
    other: 'Другое',
    registration: 'Регистрационный номер',
    purchaseDate: 'Дата покупки',
    manufacturingDate: 'Дата производства',
    ctDate: 'Дата техосмотра',
    ctOverdue: 'Техосмотр просрочен',
    ctOk: 'Техосмотр в норме',
    pollutionDate: 'Дата контроля загрязнения',
    pollutionOverdue: 'Загрязнение просрочено',
    pollutionOk: 'Загрязнение в норме',
    pollutionWarning: 'Загрязнение скоро',
    currentMileage: 'Текущий пробег',
    currentHours: 'Текущие часы',
    maintenanceInterval: 'Интервал обслуживания (км)',
    maintenanceIntervalHours: 'Интервал обслуживания (часы)',
    sparePartsRefs: 'Артикулы запчастей',
    sparePartsPhotos: 'Фото артикулов',
    notes: 'Заметки',
    save: 'Сохранить',
    cancel: 'Отмена',
    delete: 'Удалить',
    confirmDelete: 'Вы уверены, что хотите удалить это оборудование?',
    archive: 'Архивировать',
    unarchive: 'Восстановить',
    confirmArchive: 'Архивировать это оборудование (продано / выведено из эксплуатации)? Оно исчезнет из активного списка, но история сохранится.',
    archived: 'В архиве',
    showArchived: 'Показать архив',
    hideArchived: 'Скрыть архив',
    archivedSection: 'Архив оборудования',
    maintenanceHistory: 'История обслуживания',
    soldOn: 'Продано',
    soldDatePrompt: 'Дата продажи (ГГГГ-ММ-ДД), пусто = сегодня:',
    addMaintenance: 'Добавить обслуживание',
    editMaintenance: 'Редактировать обслуживание',
    clickToAddFile: 'Нажмите, чтобы добавить фото или PDF',
    pdfDocument: 'PDF документ',
    maintenanceDate: 'Дата обслуживания',
    maintenanceType: 'Тип обслуживания (несколько вариантов)',
    oilChange: 'Замена масла',
    oilFilter: 'Масляный фильтр',
    airFilter: 'Воздушный фильтр',
    fuelFilter: 'Топливный фильтр',
    cabinFilter: 'Салонный фильтр',
    brakes: 'Тормоза',
    tires: 'Шины',
    technicalControl: 'Техосмотр',
    repair: 'Ремонт',
    otherMaintenance: 'Другое',
    mileageAtMaintenance: 'Пробег',
    hoursAtMaintenance: 'Часы',
    description: 'Описание',
    invoice: 'Счёт',
    cost: 'Стоимость (€)',
    kmRemaining: 'км осталось',
    hoursRemaining: 'ч осталось',
    kmOverdue: 'км просрочки',
    hoursOverdue: 'ч просрочки',
    maintenanceOk: 'ТО в норме',
    maintenanceWarning: 'ТО скоро',
    maintenanceOverdue: 'ТО просрочено',
    updateMileage: 'Обновить пробег',
    updateHours: 'Обновить часы',
    updateKmIn: 'Следующее обновление пробега через',
    updateHoursIn: 'Следующее обновление часов через',
    updateDueNow: 'Обновление требуется сейчас',
    daysWord: 'дн.',
    noEquipment: 'Оборудование не найдено',
    photo: 'Фото',
    clickToAddPhoto: 'Нажмите, чтобы добавить фото',
    loading: 'Загрузка...',
    error: 'Ошибка',
    success: 'Успешно',
    mileageUpdatedThanks: 'Пробег обновлён. Спасибо!',
    hoursUpdatedThanks: 'Часы обновлены. Спасибо!',
    km: 'км',
    hours: 'ч',
    trackingUnit: 'Единица учёта',
    trackingKm: 'Километры',
    trackingHours: 'Часы',
    lastMaintenance: 'Последнее ТО',
    nextMaintenance: 'Следующее ТО',
    enterNewMileage: 'Введите новый пробег',
    enterNewHours: 'Введите новые часы',
    previous: 'Было',
    mileageMustBeHigher: 'Новый пробег должен быть больше старого',
    hoursMustBeHigher: 'Новые часы должны быть больше старых',
  },
  md: {
    title: 'Întreținere echipamente',
    addEquipment: 'Adaugă echipament',
    editEquipment: 'Modifică echipamentul',
    name: 'Denumire',
    type: 'Tip',
    vehicle: 'Vehicul',
    excavator: 'Excavator / Mini excavator',
    tool: 'Unealtă motorizată',
    other: 'Altul',
    registration: 'Număr înmatriculare',
    purchaseDate: 'Data achiziției',
    manufacturingDate: 'Data fabricației',
    ctDate: 'Data inspecției tehnice',
    ctOverdue: 'ITP depășit',
    ctOk: 'ITP valid',
    pollutionDate: 'Data controlului poluării',
    pollutionOverdue: 'Poluare depășită',
    pollutionOk: 'Poluare validă',
    pollutionWarning: 'Poluare în curând',
    currentMileage: 'Kilometraj actual',
    currentHours: 'Ore actuale',
    maintenanceInterval: 'Interval întreținere (km)',
    maintenanceIntervalHours: 'Interval întreținere (ore)',
    sparePartsRefs: 'Referințe piese de schimb',
    sparePartsPhotos: 'Foto referințe',
    notes: 'Note',
    save: 'Salvează',
    cancel: 'Anulează',
    delete: 'Șterge',
    confirmDelete: 'Sigur doriți să ștergeți acest echipament?',
    archive: 'Arhivează',
    unarchive: 'Reactivează',
    confirmArchive: 'Arhivați acest echipament (vândut / scos din uz)? Va dispărea din lista activă, dar istoricul va fi păstrat.',
    archived: 'Arhivat',
    showArchived: 'Vezi arhivate',
    hideArchived: 'Ascunde arhivate',
    archivedSection: 'Echipamente arhivate',
    maintenanceHistory: 'Istoric întreținere',
    soldOn: 'Vândut la',
    soldDatePrompt: 'Data vânzării (AAAA-LL-ZZ), gol = azi:',
    addMaintenance: 'Adaugă întreținere',
    editMaintenance: 'Modifică întreținerea',
    clickToAddFile: 'Click pentru a adăuga foto sau PDF',
    pdfDocument: 'Document PDF',
    maintenanceDate: 'Data întreținerii',
    maintenanceType: 'Tip întreținere (mai multe opțiuni)',
    oilChange: 'Schimb ulei',
    oilFilter: 'Filtru ulei',
    airFilter: 'Filtru aer',
    fuelFilter: 'Filtru motorină',
    cabinFilter: 'Filtru habitaclu',
    brakes: 'Frâne',
    tires: 'Anvelope',
    technicalControl: 'Inspecție tehnică',
    repair: 'Reparație',
    otherMaintenance: 'Altul',
    mileageAtMaintenance: 'Kilometraj',
    hoursAtMaintenance: 'Ore',
    description: 'Descriere',
    invoice: 'Factură',
    cost: 'Cost (€)',
    kmRemaining: 'km rămași',
    hoursRemaining: 'ore rămase',
    kmOverdue: 'km depășire',
    hoursOverdue: 'ore depășire',
    maintenanceOk: 'Întreținere la zi',
    maintenanceWarning: 'Întreținere curând',
    maintenanceOverdue: 'Întreținere depășită',
    updateMileage: 'Actualizează kilometrajul',
    updateHours: 'Actualizează orele',
    updateKmIn: 'Următoarea actualizare a kilometrajului în',
    updateHoursIn: 'Următoarea actualizare a orelor în',
    updateDueNow: 'Actualizare necesară acum',
    daysWord: 'zile',
    noEquipment: 'Niciun echipament înregistrat',
    photo: 'Foto',
    clickToAddPhoto: 'Click pentru a adăuga foto',
    loading: 'Se încarcă...',
    error: 'Eroare',
    success: 'Succes',
    mileageUpdatedThanks: 'Kilometrajul a fost actualizat. Mulțumesc !',
    hoursUpdatedThanks: 'Orele au fost actualizate. Mulțumesc !',
    km: 'km',
    hours: 'ore',
    trackingUnit: 'Unitate de urmărire',
    trackingKm: 'Kilometri',
    trackingHours: 'Ore',
    lastMaintenance: 'Ultima întreținere',
    nextMaintenance: 'Următoarea întreținere',
    enterNewMileage: 'Introduceți noul kilometraj',
    enterNewHours: 'Introduceți noile ore',
    previous: 'Anterior',
    mileageMustBeHigher: 'Noul kilometraj trebuie să fie mai mare decât cel vechi',
    hoursMustBeHigher: 'Noile ore trebuie să fie mai mari decât cele vechi',
  },
  uk: {
    title: 'Обслуговування обладнання',
    addEquipment: 'Додати обладнання',
    editEquipment: 'Редагувати обладнання',
    name: 'Назва',
    type: 'Тип',
    vehicle: 'Транспорт',
    excavator: 'Екскаватор / Міні-екскаватор',
    tool: 'Моторизований інструмент',
    other: 'Інше',
    registration: 'Реєстраційний номер',
    purchaseDate: 'Дата придбання',
    manufacturingDate: 'Дата виробництва',
    ctDate: 'Дата техогляду',
    ctOverdue: 'Техогляд прострочений',
    ctOk: 'Техогляд дійсний',
    pollutionDate: 'Дата контролю забруднення',
    pollutionOverdue: 'Забруднення прострочено',
    pollutionOk: 'Забруднення дійсне',
    pollutionWarning: 'Забруднення скоро',
    currentMileage: 'Поточний пробіг',
    currentHours: 'Поточні години',
    maintenanceInterval: 'Інтервал обслуговування (км)',
    maintenanceIntervalHours: 'Інтервал обслуговування (години)',
    sparePartsRefs: 'Артикули запчастин',
    sparePartsPhotos: 'Фото артикулів',
    notes: 'Нотатки',
    save: 'Зберегти',
    cancel: 'Скасувати',
    delete: 'Видалити',
    confirmDelete: 'Ви впевнені, що хочете видалити це обладнання?',
    archive: 'Архівувати',
    unarchive: 'Відновити',
    confirmArchive: 'Архівувати це обладнання (продано / виведено з експлуатації)? Воно зникне з активного списку, але історія збережеться.',
    archived: 'В архіві',
    showArchived: 'Показати архів',
    hideArchived: 'Сховати архів',
    archivedSection: 'Архів обладнання',
    maintenanceHistory: 'Історія обслуговування',
    soldOn: 'Продано',
    soldDatePrompt: 'Дата продажу (РРРР-ММ-ДД), порожньо = сьогодні:',
    addMaintenance: 'Додати обслуговування',
    editMaintenance: 'Редагувати обслуговування',
    clickToAddFile: 'Натисніть, щоб додати фото або PDF',
    pdfDocument: 'PDF документ',
    maintenanceDate: 'Дата обслуговування',
    maintenanceType: 'Тип обслуговування (кілька варіантів)',
    oilChange: 'Заміна масла',
    oilFilter: 'Масляний фільтр',
    airFilter: 'Повітряний фільтр',
    fuelFilter: 'Паливний фільтр',
    cabinFilter: 'Салонний фільтр',
    brakes: 'Гальма',
    tires: 'Шини',
    technicalControl: 'Техогляд',
    repair: 'Ремонт',
    otherMaintenance: 'Інше',
    mileageAtMaintenance: 'Пробіг',
    hoursAtMaintenance: 'Години',
    description: 'Опис',
    invoice: 'Рахунок',
    cost: 'Вартість (€)',
    kmRemaining: 'км залишилось',
    hoursRemaining: 'год залишилось',
    kmOverdue: 'км перевищення',
    hoursOverdue: 'год перевищення',
    maintenanceOk: 'ТО в нормі',
    maintenanceWarning: 'ТО скоро',
    maintenanceOverdue: 'ТО прострочено',
    updateMileage: 'Оновити пробіг',
    updateHours: 'Оновити години',
    updateKmIn: 'Наступне оновлення пробігу через',
    updateHoursIn: 'Наступне оновлення годин через',
    updateDueNow: 'Оновлення потрібне зараз',
    daysWord: 'дн.',
    noEquipment: 'Обладнання не знайдено',
    photo: 'Фото',
    clickToAddPhoto: 'Натисніть, щоб додати фото',
    loading: 'Завантаження...',
    error: 'Помилка',
    success: 'Успішно',
    mileageUpdatedThanks: 'Пробіг оновлено. Дякую!',
    hoursUpdatedThanks: 'Години оновлено. Дякую!',
    km: 'км',
    hours: 'год',
    trackingUnit: 'Одиниця обліку',
    trackingKm: 'Кілометри',
    trackingHours: 'Години',
    lastMaintenance: 'Останнє ТО',
    nextMaintenance: 'Наступне ТО',
    enterNewMileage: 'Введіть новий пробіг',
    enterNewHours: 'Введіть нові години',
    previous: 'Було',
    mileageMustBeHigher: 'Новий пробіг повинен бути більшим за старий',
    hoursMustBeHigher: 'Нові години повинні бути більшими за старі',
  }
};

// Equipment type icons
const typeIcons = {
  vehicle: Car,
  excavator: Truck,
  tool: Hammer,
  other: HelpCircle
};

// Equipment Card Component
function EquipmentCard({ equipment, unreadCount = 0, onSeen, t, isManager, onEdit, onDelete, onArchive, onUpdateMileage, onUpdateHours, onAddMaintenance, onViewMaintenance, onViewPhotos, onUpdateDates, autoOpenUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [showMileageDialog, setShowMileageDialog] = useState(false);
  const [showHoursDialog, setShowHoursDialog] = useState(false);

  // Arrivee depuis une notification "mise a jour km/heures" : ouvrir directement
  // la bonne boite de saisie pour cet equipement.
  useEffect(() => {
    if (autoOpenUpdate) {
      setExpanded(true);
      if ((equipment.tracking_unit || 'km') === 'hours') {
        setShowHoursDialog(true);
      } else {
        setShowMileageDialog(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenUpdate]);
  const [newMileage, setNewMileage] = useState(equipment.current_mileage || 0);
  const [newHours, setNewHours] = useState(equipment.current_hours || 0);
  const [showEditDates, setShowEditDates] = useState(false);
  const [editCtDate, setEditCtDate] = useState(equipment.ct_date || '');
  const [editPollDate, setEditPollDate] = useState(equipment.pollution_date || '');
  
  const isHoursBased = equipment.tracking_unit === 'hours';
  const TypeIcon = typeIcons[equipment.type] || HelpCircle;
  
  const statusConfig = {
    ok: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: t.maintenanceOk },
    warning: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: t.maintenanceWarning },
    overdue: { color: 'bg-red-100 text-red-700', icon: AlertTriangle, label: t.maintenanceOverdue }
  };
  
  const status = statusConfig[equipment.maintenance_status] || statusConfig.ok;
  const StatusIcon = status.icon;
  
  const handleMileageUpdate = async () => {
    if (newMileage > (equipment.current_mileage || 0)) {
      await onUpdateMileage(equipment.id, newMileage);
      setShowMileageDialog(false);
    }
  };

  const handleHoursUpdate = async () => {
    if (newHours > (equipment.current_hours || 0)) {
      await onUpdateHours(equipment.id, newHours);
      setShowHoursDialog(false);
    }
  };

  const handleSaveDates = async () => {
    try {
      await onUpdateDates(equipment.id, { ct_date: editCtDate || null, pollution_date: editPollDate || null });
      setShowEditDates(false);
    } catch {}
  };

  return (
    <div className="card-base overflow-hidden" data-testid={`equipment-${equipment.id}`}>
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => {
          // Ouvrir la fiche du vehicule efface SA pastille (Pavel 31/08/2026)
          if (!expanded && unreadCount > 0 && onSeen) onSeen();
          setExpanded(!expanded);
        }}
      >
        <div className="flex items-start gap-3">
          {/* Photo */}
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
            {equipment.photo ? (
              <img src={equipment.photo} alt={equipment.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <TypeIcon className="w-8 h-8 text-gray-400" />
              </div>
            )}
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="text-lg font-bold truncate" style={{ color: 'var(--color-primary)' }}>
                {equipment.name}
              </h3>
              {equipment.archived && (
                <span className="flex-shrink-0 h-5 px-2 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold flex items-center gap-1">
                  <Archive className="w-3 h-3" /> {t.archived}
                </span>
              )}
              {unreadCount > 0 && (
                <span
                  className="flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center animate-pulse"
                  title={`${unreadCount} notification(s)`}
                  data-testid={`equipment-badge-${equipment.id}`}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
              <span className={`badge ${status.color} flex items-center gap-1`}>
                <StatusIcon className="w-3 h-3" />
                {status.label}
              </span>
              {!equipment.archived && equipment.ct_date && (() => {
                const ctExpiry = new Date(equipment.ct_date);
                ctExpiry.setFullYear(ctExpiry.getFullYear() + 2);
                return ctExpiry < new Date();
              })() && (
                <span className="badge bg-red-100 text-red-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {t.ctOverdue}
                </span>
              )}
              {!equipment.archived && equipment.pollution_date && (() => {
                const pollExpiry = new Date(equipment.pollution_date);
                pollExpiry.setFullYear(pollExpiry.getFullYear() + 1);
                return pollExpiry < new Date();
              })() && (
                <span className="badge bg-orange-100 text-orange-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {t.pollutionOverdue}
                </span>
              )}
              {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </div>
            
            {equipment.registration && (
              <p className="text-sm text-gray-500 mb-1">{equipment.registration}</p>
            )}
            {equipment.archived && equipment.manufacturing_date && (
              <p className="text-sm text-gray-500 mb-1">
                {t.manufacturingDate} : {new Date(equipment.manufacturing_date).toLocaleDateString()}
              </p>
            )}
            {equipment.archived && (equipment.sold_date || equipment.archived_at) && (
              <p className="text-sm text-amber-700 font-medium mb-1">
                {t.soldOn} {new Date(equipment.sold_date || equipment.archived_at).toLocaleDateString()}
              </p>
            )}

            {isHoursBased ? (
              equipment.current_hours != null && (
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1 text-gray-600">
                    <Clock className="w-4 h-4" />
                    {equipment.current_hours?.toLocaleString()} {t.hours}
                  </span>
                  {!equipment.archived && equipment.hours_until_maintenance != null && (
                    <span className={`font-medium ${equipment.maintenance_status === 'overdue' ? 'text-red-600' : equipment.maintenance_status === 'warning' ? 'text-yellow-600' : 'text-green-600'}`}>
                      {equipment.maintenance_status === 'overdue'
                        ? `${Math.abs(equipment.hours_until_maintenance).toLocaleString()} ${t.hoursOverdue}`
                        : `${equipment.hours_until_maintenance?.toLocaleString()} ${t.hoursRemaining}`
                      }
                    </span>
                  )}
                </div>
              )
            ) : (
              equipment.current_mileage != null && (
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1 text-gray-600">
                    <Gauge className="w-4 h-4" />
                    {equipment.current_mileage?.toLocaleString()} {t.km}
                  </span>
                  {!equipment.archived && equipment.km_until_maintenance != null && (
                    <span className={`font-medium ${equipment.maintenance_status === 'overdue' ? 'text-red-600' : equipment.maintenance_status === 'warning' ? 'text-yellow-600' : 'text-green-600'}`}>
                      {equipment.maintenance_status === 'overdue'
                        ? `${Math.abs(equipment.km_until_maintenance).toLocaleString()} ${t.kmOverdue}`
                        : `${equipment.km_until_maintenance?.toLocaleString()} ${t.kmRemaining}`
                      }
                    </span>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t px-4 py-3 space-y-3 bg-gray-50">
          {/* Dates - vertical list for clarity */}
          <div className="space-y-2 text-sm">
            {equipment.manufacturing_date && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
                <span className="text-gray-500 text-xs sm:text-sm">{t.manufacturingDate}</span>
                <span className="font-medium">{new Date(equipment.manufacturing_date).toLocaleDateString()}</span>
              </div>
            )}
            {equipment.purchase_date && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
                <span className="text-gray-500 text-xs sm:text-sm">{t.purchaseDate}</span>
                <span className="font-medium">{new Date(equipment.purchase_date).toLocaleDateString()}</span>
              </div>
            )}
            {!equipment.archived && equipment.ct_date && (() => {
              const ctDate = new Date(equipment.ct_date);
              const ctExpiry = new Date(ctDate);
              ctExpiry.setFullYear(ctExpiry.getFullYear() + 2);
              const isCtOverdue = ctExpiry < new Date();
              return (
                <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
                  <span className="text-gray-500 text-xs sm:text-sm">{t.ctDate}</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{ctDate.toLocaleDateString()}</span>
                    <span className="text-xs text-gray-400">→</span>
                    <span className="font-medium">{ctExpiry.toLocaleDateString()}</span>
                    {isCtOverdue ? (
                      <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">{t.ctOverdue}</span>
                    ) : (
                      <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">{t.ctOk}</span>
                    )}
                  </div>
                </div>
              );
            })()}
            {!equipment.archived && equipment.pollution_date && (() => {
              const pollDate = new Date(equipment.pollution_date);
              const pollExpiry = new Date(pollDate);
              pollExpiry.setFullYear(pollExpiry.getFullYear() + 1);
              const now = new Date();
              const daysUntil = Math.ceil((pollExpiry - now) / (1000 * 60 * 60 * 24));
              const isPollOverdue = daysUntil < 0;
              const isPollWarning = !isPollOverdue && daysUntil <= 30;
              return (
                <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
                  <span className="text-gray-500 text-xs sm:text-sm">{t.pollutionDate}</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{pollDate.toLocaleDateString()}</span>
                    <span className="text-xs text-gray-400">→</span>
                    <span className="font-medium">{pollExpiry.toLocaleDateString()}</span>
                    {isPollOverdue ? (
                      <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">{t.pollutionOverdue}</span>
                    ) : isPollWarning ? (
                      <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">{t.pollutionWarning} ({daysUntil}j)</span>
                    ) : (
                      <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">{t.pollutionOk}</span>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
          {!equipment.archived && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {isHoursBased ? (
              <div>
                <span className="text-gray-500">{t.maintenanceIntervalHours}:</span>
                <span className="ml-2 font-medium">{equipment.maintenance_interval_hours?.toLocaleString()} h</span>
              </div>
            ) : (
              <div>
                <span className="text-gray-500">{t.maintenanceInterval}:</span>
                <span className="ml-2 font-medium">{equipment.maintenance_interval_min?.toLocaleString()} - {equipment.maintenance_interval_max?.toLocaleString()} km</span>
              </div>
            )}
          </div>
          )}

          {equipment.spare_parts_refs && (
            <div className="text-sm">
              <span className="text-gray-500">{t.sparePartsRefs}:</span>
              <p className="mt-1 text-gray-700 whitespace-pre-wrap">{equipment.spare_parts_refs}</p>
            </div>
          )}
          
          {/* Photos des références de pièces */}
          {equipment.spare_parts_photos?.length > 0 && (
            <div className="text-sm">
              <span className="text-gray-500">{t.sparePartsPhotos || 'Photos des références'}:</span>
              <div className="flex gap-2 flex-wrap mt-2">
                {equipment.spare_parts_photos.map((photo, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => { e.stopPropagation(); onViewPhotos(equipment.spare_parts_photos, idx); }}
                    className="w-12 h-12 rounded-lg overflow-hidden border hover:border-blue-500 transition-colors"
                  >
                    <img src={photo} alt={`Reference ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {equipment.notes && (
            <div className="text-sm">
              <span className="text-gray-500">{t.notes}:</span>
              <p className="mt-1 text-gray-700 whitespace-pre-wrap">{equipment.notes}</p>
            </div>
          )}
          
          {/* Countdown before the required km/hours update (30-day cycle) */}
          {!equipment.archived && (() => {
            const updatedAt = isHoursBased
              ? (equipment.hours_updated_at || equipment.created_at)
              : (equipment.mileage_updated_at || equipment.created_at);
            if (!updatedAt) return null;
            const daysLeft = 30 - Math.floor((Date.now() - new Date(updatedAt).getTime()) / 86400000);
            return (
              <div
                className={`text-sm flex items-center gap-1.5 rounded-lg px-2 py-1.5 border ${
                  daysLeft <= 0
                    ? 'text-red-700 bg-red-50 border-red-200 font-semibold'
                    : daysLeft <= 7
                      ? 'text-amber-700 bg-amber-50 border-amber-200'
                      : 'text-gray-600 bg-gray-50 border-gray-200'
                }`}
                data-testid="update-countdown"
              >
                <Clock className="w-4 h-4 flex-shrink-0" />
                {daysLeft <= 0
                  ? t.updateDueNow
                  : `${isHoursBased ? t.updateHoursIn : t.updateKmIn} ${daysLeft} ${t.daysWord}`}
              </div>
            );
          })()}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2">
            {!equipment.archived && (isHoursBased ? (
              <button
                onClick={(e) => { e.stopPropagation(); setShowHoursDialog(true); }}
                className="btn-secondary text-sm flex items-center gap-1"
                data-testid="update-hours-btn"
              >
                <Clock className="w-4 h-4" />
                {t.updateHours}
              </button>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); setShowMileageDialog(true); }}
                className="btn-secondary text-sm flex items-center gap-1"
                data-testid="update-mileage-btn"
              >
                <Gauge className="w-4 h-4" />
                {t.updateMileage}
              </button>
            ))}

            <button
              onClick={(e) => { e.stopPropagation(); onViewMaintenance(equipment); }}
              className="btn-secondary text-sm flex items-center gap-1"
              data-testid="view-maintenance-btn"
            >
              <FileText className="w-4 h-4" />
              {t.maintenanceHistory}
            </button>
            
            {isManager && (
              <>
                {!equipment.archived && (
                <button
                  onClick={(e) => { e.stopPropagation(); setEditCtDate(equipment.ct_date || ''); setEditPollDate(equipment.pollution_date || ''); setShowEditDates(true); }}
                  className="btn-secondary text-sm flex items-center gap-1 text-blue-600 hover:bg-blue-50"
                >
                  <Calendar className="w-4 h-4" /> CT / Pollution
                </button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); onEdit(equipment); }}
                  className="btn-secondary text-sm flex items-center gap-1"
                  data-testid="edit-equipment-btn"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onArchive(equipment); }}
                  className="btn-secondary text-sm flex items-center gap-1 text-amber-600 hover:bg-amber-50"
                  data-testid="archive-equipment-btn"
                  title={equipment.archived ? t.unarchive : t.archive}
                >
                  {equipment.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(equipment); }}
                  className="btn-secondary text-sm flex items-center gap-1 text-red-600 hover:bg-red-50"
                  data-testid="delete-equipment-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Mileage Update Dialog */}
      <Dialog open={showMileageDialog} onOpenChange={setShowMileageDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.updateMileage}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t.currentMileage} ({t.km})
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={newMileage || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setNewMileage(value === '' ? '' : parseInt(value));
                }}
                className="input-field text-lg font-semibold"
                placeholder={t.enterNewMileage}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t.previous}: {equipment.current_mileage?.toLocaleString() || 0} {t.km}
              </p>
              {newMileage && newMileage <= (equipment.current_mileage || 0) && (
                <p className="text-xs text-red-500 mt-1">
                  {t.mileageMustBeHigher}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowMileageDialog(false)} className="btn-secondary flex-1">
                {t.cancel}
              </button>
              <button 
                onClick={handleMileageUpdate}
                disabled={!newMileage || newMileage <= (equipment.current_mileage || 0)}
                className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t.save}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hours Update Dialog */}
      <Dialog open={showHoursDialog} onOpenChange={setShowHoursDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.updateHours}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t.currentHours} ({t.hours})
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={newHours || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setNewHours(value === '' ? '' : parseInt(value));
                }}
                className="input-field text-lg font-semibold"
                placeholder={t.enterNewHours}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t.previous}: {equipment.current_hours?.toLocaleString() || 0} {t.hours}
              </p>
              {newHours && newHours <= (equipment.current_hours || 0) && (
                <p className="text-xs text-red-500 mt-1">
                  {t.hoursMustBeHigher}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowHoursDialog(false)} className="btn-secondary flex-1">
                {t.cancel}
              </button>
              <button
                onClick={handleHoursUpdate}
                disabled={!newHours || newHours <= (equipment.current_hours || 0)}
                className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t.save}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* CT / Pollution Date Update Dialog */}
      <Dialog open={showEditDates} onOpenChange={setShowEditDates}>
        <DialogContent className="sm:max-w-sm" onOpenAutoFocus={e => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              CT / Pollution — {equipment.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium">{t.ctDate || 'Date contrôle technique'}</Label>
              <Input type="date" value={editCtDate} onChange={e => setEditCtDate(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-sm font-medium">{t.pollutionDate || 'Date contrôle pollution'}</Label>
              <Input type="date" value={editPollDate} onChange={e => setEditPollDate(e.target.value)} className="mt-1" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowEditDates(false)} className="btn-secondary flex-1">{t.cancel}</button>
              <button onClick={handleSaveDates} className="btn-primary flex-1">{t.save}</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

import { markOrphanNotificationsSeen, useEntityBadges } from '@/lib/moduleBadges';

// Types de notifications qui alimentent les pastilles de cet ecran.
const EQUIPMENT_NOTIF_TYPES = ['equipment', 'equipment_update_reminder'];

export default function EquipmentPage() {
  // Ouvrir le module n'efface PLUS toutes les notifications d'un coup : depuis
  // le 31/08/2026 chaque vehicule porte sa propre pastille, qui ne s'efface que
  // lorsqu'on ouvre CE vehicule. Seules les anciennes notifications, qui ne
  // sont rattachees a aucun vehicule, sont marquees lues a l'ouverture (sinon
  // elles resteraient rouges pour toujours, sans ligne ou les afficher).
  useEffect(() => { markOrphanNotificationsSeen(EQUIPMENT_NOTIF_TYPES); }, []);
  const { entityBadges, clearEntityBadge } = useEntityBadges(EQUIPMENT_NOTIF_TYPES);
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = translations[language] || translations.fr;
  const isManager = user?.role === 'manager';
  // ?update=<id> (posé par la notification de rappel) : ouvre directement la
  // boite de mise a jour km/heures de l'equipement concerne.
  const autoUpdateId = new URLSearchParams(window.location.search).get('update');
  
  const [equipment, setEquipment] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [showAddMaintenance, setShowAddMaintenance] = useState(false);
  const [editingMaintenance, setEditingMaintenance] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null); // For viewing invoice photos (array)

  // Télécharge une facture d'entretien (image base64) sur l'appareil.
  // On passe par un Blob : le téléchargement direct d'une data-URL est ignoré
  // par la plupart des navigateurs mobiles / PWA.
  const downloadInvoice = (dataUrl, index) => {
    if (!dataUrl) { toast.error("Facture introuvable"); return; }
    try {
      let blob, ext = 'jpg';
      if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) {
        const m = /^data:([\w/.+-]+);base64,(.*)$/.exec(dataUrl);
        if (!m) { window.open(dataUrl, '_blank'); return; }
        ext = (m[1].split('/')[1] || 'jpg').replace('jpeg', 'jpg');
        const bytes = atob(m[2]);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        blob = new Blob([arr], { type: m[1] });
      } else {
        // URL distante : on ouvre simplement dans un onglet
        window.open(dataUrl, '_blank');
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facture-entretien-${index + 1}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Facture téléchargée");
    } catch (e) {
      // Dernier repli : ouverture dans un nouvel onglet (l'utilisateur enregistre à la main)
      try { window.open(dataUrl, '_blank'); }
      catch (_) { toast.error("Téléchargement impossible"); }
    }
  };

  // Télécharge toutes les factures de l'entretien affiché, l'une après l'autre
  const downloadAllInvoices = (photos) => {
    if (!Array.isArray(photos)) { downloadInvoice(photos, 0); return; }
    photos.forEach((p, i) => setTimeout(() => downloadInvoice(p, i), i * 350));
  };
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0); // For navigating multiple photos
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'vehicle',
    photo: null,
    registration: '',
    purchase_date: '',
    manufacturing_date: '',
    ct_date: '',
    pollution_date: '',
    tracking_unit: 'km', // 'km' or 'hours'
    current_mileage: '',
    maintenance_interval_min: 10000,
    maintenance_interval_max: 15000,
    current_hours: '',
    maintenance_interval_hours: 500,
    spare_parts_refs: '',
    spare_parts_photos: [], // Multiple photos for spare parts references
    notes: ''
  });
  
  // Maintenance form state - types is now an array for multiple selections
  const [maintenanceForm, setMaintenanceForm] = useState({
    date: new Date().toISOString().split('T')[0],
    types: [], // Array of selected types
    otherDescription: '', // Description when "other" is selected
    mileage: '',
    hours: '',
    description: '',
    invoice_photos: [], // Multiple invoice/receipt photos
    cost: ''
  });
  
  const fetchEquipment = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/equipment?include_archived=true`);
      setEquipment(response.data);
    } catch (error) {
      console.error('Failed to fetch equipment:', error);
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  }, [t.error]);
  
  useEffect(() => {
    fetchEquipment();
    // Check maintenance reminders on page load
    axios.post(`${API}/equipment/check-maintenance-reminders`).catch(() => {});
  }, [fetchEquipment]);
  
  // Handle single photo for equipment main photo
  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      try {
        const img = new window.Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const scale = Math.min(1, 800 / Math.max(img.width, img.height));
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            const compressedData = canvas.toDataURL('image/jpeg', 0.8);
            setFormData(prev => ({ ...prev, photo: compressedData }));
          } catch (error) {
            console.error('Error compressing image:', error);
            toast.error('Erreur lors du traitement de l\'image');
          }
        };
        img.onerror = () => {
          toast.error('Erreur lors du chargement de l\'image');
        };
        img.src = reader.result;
      } catch (error) {
        toast.error('Erreur lors du traitement du fichier');
      }
    };
    reader.readAsDataURL(file);
  };
  
  // Handle multiple photos (for invoices and spare parts)
  const handleMultiplePhotos = (e, type) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    files.forEach(file => {
      const reader = new FileReader();
      // Non-image files (PDF, etc.): store the data URL as-is, no canvas compression.
      const isImage = (file.type || '').startsWith('image/');
      if (!isImage) {
        if (file.size > 8 * 1024 * 1024) {
          toast.error('Fichier trop volumineux (8 Mo max)');
          return;
        }
        reader.onloadend = () => {
          const data = reader.result;
          if (type === 'invoice') {
            setMaintenanceForm(prev => ({ ...prev, invoice_photos: [...prev.invoice_photos, data] }));
          } else if (type === 'spare_parts') {
            setFormData(prev => ({ ...prev, spare_parts_photos: [...prev.spare_parts_photos, data] }));
          }
        };
        reader.readAsDataURL(file);
        return;
      }
      reader.onloadend = () => {
        try {
          const img = new window.Image();
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              const scale = Math.min(1, 800 / Math.max(img.width, img.height));
              canvas.width = img.width * scale;
              canvas.height = img.height * scale;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              const compressedData = canvas.toDataURL('image/jpeg', 0.7);

              if (type === 'invoice') {
                setMaintenanceForm(prev => ({
                  ...prev,
                  invoice_photos: [...prev.invoice_photos, compressedData]
                }));
              } else if (type === 'spare_parts') {
                setFormData(prev => ({
                  ...prev,
                  spare_parts_photos: [...prev.spare_parts_photos, compressedData]
                }));
              }
            } catch (error) {
              toast.error('Erreur lors du traitement de l\'image');
            }
          };
          img.onerror = () => {
            toast.error('Erreur lors du chargement de l\'image');
          };
          img.src = reader.result;
        } catch (error) {
          toast.error('Erreur lors du traitement du fichier');
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // True when a stored attachment is a PDF (data URL or remote path).
  const isPdfAttachment = (data) =>
    typeof data === 'string' && (data.startsWith('data:application/pdf') || /\.pdf(\?|$)/i.test(data));
  
  // Remove photo from array
  const removePhoto = (type, index) => {
    if (type === 'invoice') {
      setMaintenanceForm(prev => ({
        ...prev,
        invoice_photos: prev.invoice_photos.filter((_, i) => i !== index)
      }));
    } else if (type === 'spare_parts') {
      setFormData(prev => ({
        ...prev,
        spare_parts_photos: prev.spare_parts_photos.filter((_, i) => i !== index)
      }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        current_mileage: formData.current_mileage ? parseInt(formData.current_mileage) : null,
        maintenance_interval_min: parseInt(formData.maintenance_interval_min),
        maintenance_interval_max: parseInt(formData.maintenance_interval_max),
        current_hours: formData.current_hours ? parseInt(formData.current_hours) : null,
        maintenance_interval_hours: formData.maintenance_interval_hours ? parseInt(formData.maintenance_interval_hours) : 500,
      };
      
      if (editingEquipment) {
        await axios.put(`${API}/equipment/${editingEquipment.id}`, payload);
        toast.success(t.success);
      } else {
        await axios.post(`${API}/equipment`, payload);
        toast.success(t.success);
      }
      
      setShowForm(false);
      setEditingEquipment(null);
      resetForm();
      fetchEquipment();
    } catch (error) {
      console.error('Failed to save equipment:', error);
      toast.error(error.response?.data?.detail || t.error);
    }
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      type: 'vehicle',
      photo: null,
      registration: '',
      purchase_date: '',
      manufacturing_date: '',
      ct_date: '',
      pollution_date: '',
      tracking_unit: 'km',
      current_mileage: '',
      maintenance_interval_min: 10000,
      maintenance_interval_max: 15000,
      current_hours: '',
      maintenance_interval_hours: 500,
      spare_parts_refs: '',
      spare_parts_photos: [],
      notes: ''
    });
  };
  
  const handleEdit = (eq) => {
    setEditingEquipment(eq);
    setFormData({
      name: eq.name,
      type: eq.type,
      photo: eq.photo,
      registration: eq.registration || '',
      purchase_date: eq.purchase_date || '',
      manufacturing_date: eq.manufacturing_date || '',
      ct_date: eq.ct_date || '',
      pollution_date: eq.pollution_date || '',
      tracking_unit: eq.tracking_unit || 'km',
      current_mileage: eq.current_mileage || '',
      maintenance_interval_min: eq.maintenance_interval_min || 10000,
      maintenance_interval_max: eq.maintenance_interval_max || 15000,
      current_hours: eq.current_hours || '',
      maintenance_interval_hours: eq.maintenance_interval_hours || 500,
      spare_parts_refs: eq.spare_parts_refs || '',
      spare_parts_photos: eq.spare_parts_photos || [],
      notes: eq.notes || ''
    });
    setShowForm(true);
  };
  
  const handleUpdateDates = async (equipmentId, data) => {
    try {
      await axios.put(`${API}/equipment/${equipmentId}/dates`, data);
      toast.success(t.success);
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.error);
    }
  };

  const handleDelete = async (eq) => {
    if (!window.confirm(t.confirmDelete)) return;
    
    try {
      await axios.delete(`${API}/equipment/${eq.id}`);
      toast.success(t.success);
      fetchEquipment();
    } catch (error) {
      console.error('Failed to delete equipment:', error);
      toast.error(error.response?.data?.detail || t.error);
    }
  };
  
  const handleArchive = async (eq) => {
    // Archiving asks for confirmation + an optional sold date; unarchiving is immediate.
    let soldDate = null;
    if (!eq.archived) {
      if (!window.confirm(t.confirmArchive)) return;
      const today = new Date().toISOString().slice(0, 10);
      const input = window.prompt(t.soldDatePrompt, today);
      if (input === null) return; // cancelled
      soldDate = (input || '').trim() || today;
    }
    try {
      await axios.put(`${API}/equipment/${eq.id}/archive`, { archived: !eq.archived, sold_date: soldDate });
      toast.success(t.success);
      fetchEquipment();
    } catch (error) {
      console.error('Failed to archive equipment:', error);
      toast.error(error.response?.data?.detail || t.error);
    }
  };

  const handleUpdateMileage = async (equipmentId, mileage) => {
    try {
      await axios.put(`${API}/equipment/${equipmentId}/mileage`, { mileage });
      toast.success(t.mileageUpdatedThanks);
      fetchEquipment();
    } catch (error) {
      console.error('Failed to update mileage:', error);
      toast.error(error.response?.data?.detail || t.error);
    }
  };
  
  const handleUpdateHours = async (equipmentId, hours) => {
    try {
      await axios.put(`${API}/equipment/${equipmentId}/hours`, { hours });
      toast.success(t.hoursUpdatedThanks);
      fetchEquipment();
    } catch (error) {
      console.error('Failed to update hours:', error);
      toast.error(error.response?.data?.detail || t.error);
    }
  };
  
  const handleViewMaintenance = async (eq) => {
    setSelectedEquipment(eq);
    try {
      const response = await axios.get(`${API}/equipment/${eq.id}/maintenance`);
      setMaintenanceRecords(response.data);
      setShowMaintenanceDialog(true);
    } catch (error) {
      console.error('Failed to fetch maintenance records:', error);
      toast.error(t.error);
    }
  };
  
  const resetMaintenanceForm = () => {
    setMaintenanceForm({
      date: new Date().toISOString().split('T')[0],
      types: [],
      otherDescription: '',
      mileage: '',
      hours: '',
      description: '',
      invoice_photos: [],
      cost: ''
    });
    setEditingMaintenance(null);
  };

  const handleEditMaintenance = (record) => {
    // Reverse the comma-joined type string: known keys stay as types, anything
    // else goes into the free-text "other" description.
    const knownKeys = ['oil_change','oil_filter','air_filter','fuel_filter','cabin_filter','brakes','tires','technical_control','repair','other'];
    const parts = record.type ? record.type.split(',').map(s => s.trim()).filter(Boolean) : [];
    const types = [];
    const customs = [];
    parts.forEach(p => {
      if (knownKeys.includes(p)) types.push(p);
      else customs.push(p);
    });
    if (customs.length && !types.includes('other')) types.push('other');
    setEditingMaintenance(record);
    setMaintenanceForm({
      date: record.date || new Date().toISOString().split('T')[0],
      types,
      otherDescription: customs.join(', '),
      mileage: record.mileage != null ? String(record.mileage) : '',
      hours: record.hours != null ? String(record.hours) : '',
      description: record.description || '',
      invoice_photos: record.invoice_photos?.length ? [...record.invoice_photos] : (record.invoice_photo ? [record.invoice_photo] : []),
      cost: record.cost != null ? String(record.cost) : ''
    });
    setShowAddMaintenance(true);
  };

  const handleAddMaintenance = async (e) => {
    e.preventDefault();
    if (!selectedEquipment) return;
    if (maintenanceForm.types.length === 0) {
      toast.error('Sélectionnez au moins un type d\'entretien');
      return;
    }

    try {
      // Build types array, replacing "other" with the custom description if provided
      const typesWithOther = maintenanceForm.types.map(type => {
        if (type === 'other' && maintenanceForm.otherDescription.trim()) {
          return maintenanceForm.otherDescription.trim();
        }
        return type;
      });

      // Join multiple types with comma
      const typeString = typesWithOther.join(', ');

      const payload = {
        equipment_id: selectedEquipment.id,
        date: maintenanceForm.date,
        type: typeString,
        mileage: maintenanceForm.mileage ? parseInt(maintenanceForm.mileage) : null,
        hours: maintenanceForm.hours ? parseInt(maintenanceForm.hours) : null,
        description: maintenanceForm.description,
        invoice_photos: maintenanceForm.invoice_photos,
        cost: maintenanceForm.cost ? parseFloat(maintenanceForm.cost) : null
      };

      if (editingMaintenance) {
        await axios.put(`${API}/equipment/${selectedEquipment.id}/maintenance/${editingMaintenance.id}`, payload);
      } else {
        await axios.post(`${API}/equipment/${selectedEquipment.id}/maintenance`, payload);
      }

      toast.success(t.success);
      setShowAddMaintenance(false);
      resetMaintenanceForm();

      // Refresh maintenance records and equipment list
      const response = await axios.get(`${API}/equipment/${selectedEquipment.id}/maintenance`);
      setMaintenanceRecords(response.data);
      fetchEquipment();
    } catch (error) {
      console.error('Failed to save maintenance:', error);
      toast.error(error.response?.data?.detail || t.error);
    }
  };
  
  const handleDeleteMaintenance = async (recordId) => {
    if (!selectedEquipment) return;
    
    try {
      await axios.delete(`${API}/equipment/${selectedEquipment.id}/maintenance/${recordId}`);
      toast.success(t.success);
      const response = await axios.get(`${API}/equipment/${selectedEquipment.id}/maintenance`);
      setMaintenanceRecords(response.data);
    } catch (error) {
      console.error('Failed to delete maintenance:', error);
      toast.error(error.response?.data?.detail || t.error);
    }
  };
  
  // Toggle maintenance type selection
  const toggleMaintenanceType = (typeKey) => {
    setMaintenanceForm(prev => ({
      ...prev,
      types: prev.types.includes(typeKey)
        ? prev.types.filter(t => t !== typeKey)
        : [...prev.types, typeKey]
    }));
  };
  
  const maintenanceTypes = {
    oil_change: t.oilChange,
    oil_filter: t.oilFilter,
    air_filter: t.airFilter,
    fuel_filter: t.fuelFilter,
    cabin_filter: t.cabinFilter,
    brakes: t.brakes,
    tires: t.tires,
    technical_control: t.technicalControl,
    repair: t.repair,
    other: t.otherMaintenance
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="equipment-page">
      {/* Header - Mobile optimized: title left, button right */}
      <div className="flex items-center justify-between mb-6 gap-2">
        <h1 className="page-title flex items-center gap-2 min-w-0 flex-1">
          <Wrench className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
          <span className="truncate text-lg sm:text-xl">{t.title}</span>
        </h1>
        {isManager && (
          <button
            onClick={() => { resetForm(); setEditingEquipment(null); setShowForm(true); }}
            className="btn-primary flex items-center justify-center min-w-10 h-10 px-3 sm:px-4 sm:py-2 rounded-full sm:rounded-lg flex-shrink-0 shadow-lg"
            data-testid="add-equipment-btn"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline ml-2">{t.addEquipment}</span>
          </button>
        )}
      </div>
      
      {/* Equipment List */}
      {(() => {
        const activeEquipment = equipment.filter((eq) => !eq.archived);
        const archivedEquipment = equipment.filter((eq) => eq.archived);
        const renderCard = (eq) => (
          <EquipmentCard
            key={eq.id}
            equipment={eq}
            unreadCount={entityBadges[eq.id] || 0}
            onSeen={() => clearEntityBadge(eq.id)}
            autoOpenUpdate={autoUpdateId === eq.id}
            t={t}
            isManager={isManager}
            onEdit={handleEdit}
            onUpdateDates={handleUpdateDates}
            onDelete={handleDelete}
            onArchive={handleArchive}
            onUpdateMileage={handleUpdateMileage}
            onUpdateHours={handleUpdateHours}
            onViewMaintenance={handleViewMaintenance}
            onViewPhotos={(photos, index) => { setViewingInvoice(photos); setCurrentPhotoIndex(index); }}
          />
        );
        return (
          <>
            {activeEquipment.length === 0 ? (
              <div className="card-base p-8 text-center text-gray-500">
                <Wrench className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{t.noEquipment}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {activeEquipment.map(renderCard)}
              </div>
            )}

            {archivedEquipment.length > 0 && (
              <div className="mt-8">
                <button
                  onClick={() => setShowArchived((v) => !v)}
                  className="btn-secondary text-sm flex items-center gap-2 text-gray-600"
                  data-testid="toggle-archived-btn"
                >
                  <Archive className="w-4 h-4" />
                  {showArchived ? t.hideArchived : t.showArchived} ({archivedEquipment.length})
                </button>
                {showArchived && (
                  <div className="space-y-4 mt-4 opacity-80">
                    {archivedEquipment.map(renderCard)}
                  </div>
                )}
              </div>
            )}
          </>
        );
      })()}
      
      {/* Add/Edit Equipment Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEquipment ? t.editEquipment : t.addEquipment}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Photo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.photo}</label>
              <label className="block w-full h-32 border-2 border-dashed rounded-xl cursor-pointer hover:border-primary transition-colors overflow-hidden">
                {formData.photo ? (
                  <img src={formData.photo} alt="Equipment" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                    <Camera className="w-8 h-8 mb-1" />
                    <span className="text-sm">{t.clickToAddPhoto}</span>
                  </div>
                )}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoChange(e)} />
              </label>
            </div>
            
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.name} *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="input-field"
                required
              />
            </div>
            
            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.type}</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                className="input-field"
              >
                <option value="vehicle">{t.vehicle}</option>
                <option value="excavator">{t.excavator}</option>
                <option value="tool">{t.tool}</option>
                <option value="other">{t.other}</option>
              </select>
            </div>
            
            {/* Tracking Unit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t.trackingUnit}</label>
              <div className="flex gap-3">
                <label className={`flex-1 flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${formData.tracking_unit === 'km' ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                  <input
                    type="radio"
                    value="km"
                    checked={formData.tracking_unit === 'km'}
                    onChange={() => setFormData(prev => ({ ...prev, tracking_unit: 'km' }))}
                    className="w-4 h-4"
                  />
                  <Gauge className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium">{t.trackingKm}</span>
                </label>
                <label className={`flex-1 flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${formData.tracking_unit === 'hours' ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                  <input
                    type="radio"
                    value="hours"
                    checked={formData.tracking_unit === 'hours'}
                    onChange={() => setFormData(prev => ({ ...prev, tracking_unit: 'hours' }))}
                    className="w-4 h-4"
                  />
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium">{t.trackingHours}</span>
                </label>
              </div>
            </div>
            
            {/* Registration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.registration}</label>
              <input
                type="text"
                value={formData.registration}
                onChange={(e) => setFormData(prev => ({ ...prev, registration: e.target.value }))}
                className="input-field"
                placeholder="AB-123-CD"
              />
            </div>
            
            {/* Manufacturing Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.manufacturingDate}</label>
              <input
                type="date"
                value={formData.manufacturing_date}
                onChange={(e) => setFormData(prev => ({ ...prev, manufacturing_date: e.target.value }))}
                className="input-field"
                data-testid="equipment-manufacturing-date"
              />
            </div>
            
            {/* Purchase Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.purchaseDate}</label>
              <input
                type="date"
                value={formData.purchase_date}
                onChange={(e) => setFormData(prev => ({ ...prev, purchase_date: e.target.value }))}
                className="input-field"
                data-testid="equipment-purchase-date"
              />
            </div>
            
            {/* CT Date - only for vehicles (km tracking) */}
            {formData.tracking_unit === 'km' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.ctDate}</label>
                <input
                  type="date"
                  value={formData.ct_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, ct_date: e.target.value }))}
                  className="input-field"
                  data-testid="equipment-ct-date"
                />
              </div>
            )}
            
            {/* Pollution Date - only for vehicles (km tracking) */}
            {formData.tracking_unit === 'km' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.pollutionDate}</label>
                <input
                  type="date"
                  value={formData.pollution_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, pollution_date: e.target.value }))}
                  className="input-field"
                  data-testid="equipment-pollution-date"
                />
              </div>
            )}
            
            {/* Current value + Interval - conditional based on tracking unit */}
            {formData.tracking_unit === 'hours' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.currentHours}</label>
                  <input
                    type="number"
                    value={formData.current_hours}
                    onChange={(e) => setFormData(prev => ({ ...prev, current_hours: e.target.value }))}
                    className="input-field"
                    placeholder="1250"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.maintenanceIntervalHours}</label>
                  <input
                    type="number"
                    value={formData.maintenance_interval_hours}
                    onChange={(e) => setFormData(prev => ({ ...prev, maintenance_interval_hours: e.target.value }))}
                    className="input-field"
                    placeholder="500"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.currentMileage}</label>
                  <input
                    type="number"
                    value={formData.current_mileage}
                    onChange={(e) => setFormData(prev => ({ ...prev, current_mileage: e.target.value }))}
                    className="input-field"
                    placeholder="50000"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.maintenanceInterval} (min)</label>
                    <input
                      type="number"
                      value={formData.maintenance_interval_min}
                      onChange={(e) => setFormData(prev => ({ ...prev, maintenance_interval_min: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">(max)</label>
                    <input
                      type="number"
                      value={formData.maintenance_interval_max}
                      onChange={(e) => setFormData(prev => ({ ...prev, maintenance_interval_max: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                </div>
              </>
            )}
            
            {/* Spare Parts Refs */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.sparePartsRefs}</label>
              <textarea
                value={formData.spare_parts_refs}
                onChange={(e) => setFormData(prev => ({ ...prev, spare_parts_refs: e.target.value }))}
                className="input-field"
                rows={3}
                placeholder="Filtre à huile: REF123&#10;Filtre à air: REF456"
              />
              
              {/* Photos des références */}
              <label className="block text-sm font-medium text-gray-700 mb-1 mt-3">
                {t.sparePartsPhotos || 'Photos des références'} {formData.spare_parts_photos.length > 0 && `(${formData.spare_parts_photos.length})`}
              </label>
              {/* Display existing photos */}
              {formData.spare_parts_photos.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-2">
                  {formData.spare_parts_photos.map((photo, idx) => (
                    <div key={idx} className="relative w-16 h-16 rounded-lg overflow-hidden border">
                      <img src={photo} alt={`Reference ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePhoto('spare_parts', idx)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {/* Add photos button */}
              <label className="block w-full h-14 border-2 border-dashed rounded-xl cursor-pointer hover:border-primary transition-colors">
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                  <Camera className="w-5 h-5 mb-1" />
                  <span className="text-xs">{t.clickToAddPhoto}</span>
                </div>
                <input 
                  type="file" 
                  accept="image/*" 
                  multiple 
                  className="hidden" 
                  onChange={(e) => handleMultiplePhotos(e, 'spare_parts')} 
                />
              </label>
            </div>
            
            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.notes}</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="input-field"
                rows={2}
              />
            </div>
            
            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary flex-1">
                {t.cancel}
              </button>
              <button type="submit" className="btn-primary flex-1">
                {t.save}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Maintenance History Dialog */}
      <Dialog open={showMaintenanceDialog} onOpenChange={setShowMaintenanceDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {t.maintenanceHistory} - {selectedEquipment?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Add maintenance button - visible for all authenticated users */}
            <button
              onClick={() => { resetMaintenanceForm(); setShowAddMaintenance(true); }}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {t.addMaintenance}
            </button>
            
            {/* Summary stats */}
            {maintenanceRecords.length > 0 && (
              <div className="grid grid-cols-2 gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{maintenanceRecords.length}</p>
                  <p className="text-xs text-gray-600">{t.maintenanceHistory?.split(' ')[0] || 'Entretiens'}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {maintenanceRecords.reduce((sum, r) => sum + (r.cost || 0), 0).toFixed(0)} €
                  </p>
                  <p className="text-xs text-gray-600">Total dépensé</p>
                </div>
              </div>
            )}
            
            {maintenanceRecords.length === 0 ? (
              <div className="text-center py-8">
                <Wrench className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Aucun entretien enregistré</p>
                <p className="text-sm text-gray-400 mt-1">Les entretiens effectués apparaîtront ici</p>
              </div>
            ) : (
              <div className="space-y-4">
                {maintenanceRecords.map((record, index) => {
                  // Handle multiple types stored as comma-separated string
                  const types = record.type ? record.type.split(', ').map(t => t.trim()) : [];
                  const displayTypes = types.map(t => maintenanceTypes[t] || t);
                  
                  return (
                  <div key={record.id} className="border rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
                    {/* Header with date and status */}
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-2 border-b flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">
                            {new Date(record.date).toLocaleDateString('fr-FR', { 
                              day: 'numeric', 
                              month: 'long', 
                              year: 'numeric' 
                            })}
                          </p>
                          {record.mileage && (
                            <p className="text-xs text-gray-500">{record.mileage.toLocaleString()} km</p>
                          )}
                          {record.hours && (
                            <p className="text-xs text-gray-500">{record.hours.toLocaleString()} h</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Photos button */}
                        {(record.invoice_photos?.length > 0 || record.invoice_photo) && (
                          <button
                            onClick={() => setViewingInvoice(record.invoice_photos?.length > 0 ? record.invoice_photos : [record.invoice_photo])}
                            className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200 transition-colors"
                            data-testid={`view-invoice-${record.id}`}
                          >
                            <Camera className="w-4 h-4" />
                            <span>{record.invoice_photos?.length || 1}</span>
                          </button>
                        )}
                        <button
                          onClick={() => handleEditMaintenance(record)}
                          className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                          data-testid={`edit-maintenance-${record.id}`}
                          title={t.editMaintenance}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {isManager && (
                          <button
                            onClick={() => handleDeleteMaintenance(record.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            data-testid={`delete-maintenance-${record.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-4 space-y-3">
                      {/* Types badges */}
                      <div className="flex flex-wrap gap-2">
                        {displayTypes.map((type, idx) => (
                          <span 
                            key={idx} 
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                          >
                            <Wrench className="w-3 h-3 mr-1" />
                            {type}
                          </span>
                        ))}
                      </div>
                      
                      {/* Description */}
                      {record.description && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-700">{record.description}</p>
                        </div>
                      )}
                      
                      {/* Cost */}
                      {record.cost && (
                        <div className="flex items-center justify-between pt-2 border-t">
                          <span className="text-sm text-gray-500">Coût de l'intervention</span>
                          <span className="text-lg font-bold text-green-600">{record.cost} €</span>
                        </div>
                      )}
                      
                      {/* Created info */}
                      <div className="flex items-center gap-1 text-xs text-gray-400 pt-1">
                        <Clock className="w-3 h-3" />
                        <span>Enregistré le {new Date(record.created_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Add Maintenance Dialog */}
      <Dialog open={showAddMaintenance} onOpenChange={(open) => { setShowAddMaintenance(open); if (!open) resetMaintenanceForm(); }}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col p-0">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle>{editingMaintenance ? t.editMaintenance : t.addMaintenance}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddMaintenance} className="flex-1 overflow-y-auto px-6 pb-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.maintenanceDate} *</label>
              <input
                type="date"
                value={maintenanceForm.date}
                onChange={(e) => setMaintenanceForm(prev => ({ ...prev, date: e.target.value }))}
                className="input-field"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t.maintenanceType}</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(maintenanceTypes).map(([key, label]) => (
                  <label 
                    key={key}
                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                      maintenanceForm.types.includes(key) 
                        ? 'bg-blue-50 border-blue-300' 
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={maintenanceForm.types.includes(key)}
                      onChange={() => toggleMaintenanceType(key)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-sm">{label}</span>
                  </label>
                ))}
              </div>
              
              {/* Show text input when "other" is selected */}
              {maintenanceForm.types.includes('other') && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Précisez l'intervention *
                  </label>
                  <input
                    type="text"
                    value={maintenanceForm.otherDescription}
                    onChange={(e) => setMaintenanceForm(prev => ({ ...prev, otherDescription: e.target.value }))}
                    className="input-field"
                    placeholder="Ex: Embrayage, Courroie de distribution, Turbo..."
                    required={maintenanceForm.types.includes('other')}
                  />
                </div>
              )}
            </div>
            
            {selectedEquipment?.tracking_unit === 'hours' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.hoursAtMaintenance}
                  {selectedEquipment?.current_hours != null && (
                    <span className="text-gray-400 font-normal ml-1">
                      (actuel: {selectedEquipment.current_hours.toLocaleString()} h)
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={maintenanceForm.hours}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setMaintenanceForm(prev => ({ ...prev, hours: value }));
                  }}
                  className="input-field"
                  placeholder="Ex: 1500"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.mileageAtMaintenance}
                  {selectedEquipment?.current_mileage && (
                    <span className="text-gray-400 font-normal ml-1">
                      (actuel: {selectedEquipment.current_mileage.toLocaleString()} km)
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={maintenanceForm.mileage}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setMaintenanceForm(prev => ({ ...prev, mileage: value }));
                  }}
                  className="input-field"
                  placeholder="Ex: 95000"
                />
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.description}</label>
              <textarea
                value={maintenanceForm.description}
                onChange={(e) => setMaintenanceForm(prev => ({ ...prev, description: e.target.value }))}
                className="input-field"
                rows={2}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.cost}</label>
              <input
                type="number"
                step="0.01"
                value={maintenanceForm.cost}
                onChange={(e) => setMaintenanceForm(prev => ({ ...prev, cost: e.target.value }))}
                className="input-field"
                placeholder="0.00"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t.invoice} {maintenanceForm.invoice_photos.length > 0 && `(${maintenanceForm.invoice_photos.length})`}
              </label>
              {/* Display existing photos */}
              {maintenanceForm.invoice_photos.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-2">
                  {maintenanceForm.invoice_photos.map((photo, idx) => (
                    <div key={idx} className="relative w-16 h-16 rounded-lg overflow-hidden border">
                      {isPdfAttachment(photo) ? (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 text-red-600">
                          <FileText className="w-6 h-6" />
                          <span className="text-[9px] font-semibold mt-0.5">PDF</span>
                        </div>
                      ) : (
                        <img src={photo} alt={`Invoice ${idx + 1}`} className="w-full h-full object-cover" />
                      )}
                      <button
                        type="button"
                        onClick={() => removePhoto('invoice', idx)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {/* Add more photos button */}
              <label className="block w-full h-16 border-2 border-dashed rounded-xl cursor-pointer hover:border-primary transition-colors">
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                  <Upload className="w-5 h-5 mb-1" />
                  <span className="text-xs">{t.clickToAddFile}</span>
                </div>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  className="hidden"
                  onChange={(e) => handleMultiplePhotos(e, 'invoice')}
                />
              </label>
            </div>
            
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => { setShowAddMaintenance(false); resetMaintenanceForm(); }} className="btn-secondary flex-1">
                {t.cancel}
              </button>
              <button type="submit" className="btn-primary flex-1">
                {t.save}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Invoice Photo Viewer Dialog - Supports multiple photos */}
      <Dialog open={!!viewingInvoice} onOpenChange={() => { setViewingInvoice(null); setCurrentPhotoIndex(0); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] p-2">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {t.invoice}
              </span>
              <span className="flex items-center gap-3">
                {viewingInvoice?.length > 1 && (
                  <span className="text-sm text-gray-500">
                    {currentPhotoIndex + 1} / {viewingInvoice.length}
                  </span>
                )}
                <button
                  onClick={() => downloadInvoice(
                    Array.isArray(viewingInvoice) ? viewingInvoice[currentPhotoIndex] : viewingInvoice,
                    currentPhotoIndex
                  )}
                  className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                  data-testid="download-invoice"
                  title="Télécharger cette facture"
                >
                  <Download className="w-4 h-4" />
                  <span>Télécharger</span>
                </button>
                {viewingInvoice?.length > 1 && (
                  <button
                    onClick={() => downloadAllInvoices(viewingInvoice)}
                    className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200 transition-colors"
                    data-testid="download-all-invoices"
                    title="Télécharger toutes les factures"
                  >
                    <Download className="w-4 h-4" />
                    <span>Tout ({viewingInvoice.length})</span>
                  </button>
                )}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-2 relative">
            {viewingInvoice?.length > 1 && currentPhotoIndex > 0 && (
              <button
                onClick={() => setCurrentPhotoIndex(prev => prev - 1)}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 z-10"
              >
                <ChevronDown className="w-6 h-6 rotate-90" />
              </button>
            )}
            {viewingInvoice && (() => {
              const current = Array.isArray(viewingInvoice) ? viewingInvoice[currentPhotoIndex] : viewingInvoice;
              return isPdfAttachment(current) ? (
                <iframe
                  src={current}
                  title={`Document ${currentPhotoIndex + 1}`}
                  className="w-full h-[70vh] rounded-lg border"
                />
              ) : (
                <img
                  src={current}
                  alt={`Invoice ${currentPhotoIndex + 1}`}
                  className="max-w-full max-h-[70vh] object-contain rounded-lg"
                />
              );
            })()}
            {viewingInvoice?.length > 1 && currentPhotoIndex < viewingInvoice.length - 1 && (
              <button
                onClick={() => setCurrentPhotoIndex(prev => prev + 1)}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 z-10"
              >
                <ChevronDown className="w-6 h-6 -rotate-90" />
              </button>
            )}
          </div>
          {/* Thumbnails for multiple photos */}
          {viewingInvoice?.length > 1 && (
            <div className="flex gap-2 justify-center mt-2 overflow-x-auto pb-2">
              {viewingInvoice.map((photo, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentPhotoIndex(idx)}
                  className={`w-12 h-12 rounded border-2 overflow-hidden flex-shrink-0 ${
                    idx === currentPhotoIndex ? 'border-blue-500' : 'border-gray-200'
                  }`}
                >
                  {isPdfAttachment(photo) ? (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 text-red-600">
                      <FileText className="w-4 h-4" />
                      <span className="text-[8px] font-semibold">PDF</span>
                    </div>
                  ) : (
                    <img src={photo} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                  )}
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
